import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotificari } from '../../hooks/useNotificari';
import { useWindowWidth } from '../../hooks/useWindowWidth';

export default function Layout({ children }) {
  const { user, logout, isFunctionar, isAdmin } = useAuth();
  const { necitite } = useNotificari();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const w = useWindowWidth();
  const isMobile = w < 768;

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  const isCetatean = !isFunctionar;

  // ── Navigare cetățean ─────────────────────────────────────────
  const navCetatean = [
    { to: '/dashboard',   label: 'Acasă',        icon: '🏠' },
    { to: '/cereri',      label: 'Cererile mele', icon: '📋' },
    { to: '/cereri/noua', label: 'Cerere nouă',   icon: '✏️' },
    { to: '/cereri?status=arhivata', label: 'Arhivă', icon: '🗄️' },
    { to: '/plati',       label: 'Plăți online',  icon: '💳' },
    { to: '/programari',  label: 'Programare',    icon: '📅' },
    { to: '/impozite',    label: 'Impozite',       icon: '🏛' },
    { to: '/formulare',   label: 'Formulare',      icon: '📄' },
    { to: '/profil',      label: 'Profilul meu',  icon: '👤' },
  ];

  // ── Navigare angajat/admin ────────────────────────────────────
  const navAngajat = [
    { to: '/dashboard',   label: 'Acasă',         icon: '🏠' },
    { to: '/cereri',      label: 'Cereri',         icon: '📋' },
    { to: '/cereri?status=arhivata', label: 'Arhivă', icon: '🗄️' },
    { to: '/analytics',   label: 'Statistici',     icon: '📊', functionar: true },
    { to: '/admin',       label: 'Administrare',   icon: '⚙️', admin: true },
    { to: '/profil',      label: 'Profil',          icon: '👤' },
  ].filter(item => {
    if (item.admin && !isAdmin) return false;
    if (item.functionar && !isFunctionar) return false;
    return true;
  });

  const navItems = isCetatean ? navCetatean : navAngajat;
  const theme = isCetatean ? thC : thA;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: isCetatean ? '#f0f4f8' : '#f4f6f9' }}>

      {/* ── Sidebar desktop ─────────────────────────────────────── */}
      {!isMobile && (
        <aside style={{ ...theme.sidebar, width: '220px', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          {/* Brand */}
          <div style={theme.brand}>
            <span style={{ fontSize: '24px' }}>🏛</span>
            <div>
              <div style={theme.brandText}>{isCetatean ? 'Portal Cetățean' : 'Intranet'}</div>
              <div style={theme.brandSub}>{user?.primaria || 'Primăria'}</div>
            </div>
          </div>

          {/* User card */}
          <div style={theme.userCard}>
            <span style={{ fontSize: '28px' }}>{isCetatean ? (user?.tip_persoana === 'juridica' ? '🏢' : '👤') : '🛠'}</span>
            <div style={{ minWidth: 0 }}>
              <div style={theme.userName}>{user?.prenume} {user?.nume}</div>
              <div style={theme.userRole}>{isCetatean ? 'Cetățean' : (user?.rol || '')}</div>
            </div>
          </div>

          <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '8px 0', gap: '2px', overflowY: 'auto' }}>
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                style={({ isActive }) => ({ ...theme.navLink, ...(isActive ? theme.navActive : {}) })}
              >
                <span style={{ width: '22px', textAlign: 'center', fontSize: '16px', flexShrink: 0 }}>{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          <button onClick={handleLogout} style={theme.sidebarLogout}>
            ↩ Deconectare
          </button>
        </aside>
      )}

      {/* ── Conținut principal ──────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* Top bar */}
        <header style={{ ...theme.topBar, height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', position: 'sticky', top: 0, zIndex: 100 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {isMobile && (
              <button onClick={() => setMenuOpen(!menuOpen)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: theme.topBarTextColor, padding: '4px' }}>
                {menuOpen ? '✕' : '☰'}
              </button>
            )}
            {isMobile
              ? <span style={{ fontWeight: '700', color: theme.topBarTextColor, fontSize: '16px' }}>
                  {isCetatean ? 'Portal Cetățean' : 'Intranet'}
                </span>
              : <span style={{ fontSize: '14px', color: theme.topBarTextColor, fontWeight: '500' }}>
                  Bun venit, {user?.prenume || ''}!
                </span>
            }
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Bell notificări cetățean */}
            {isCetatean && (
              <NavLink to="/profil" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px', borderRadius: '50%', backgroundColor: isCetatean ? '#e0e7ff' : '#2d2d4e', textDecoration: 'none' }} title="Notificări">
                <span style={{ fontSize: '18px' }}>🔔</span>
                {necitite > 0 && (
                  <span style={{ position: 'absolute', top: '3px', right: '3px', background: '#e24b4a', color: '#fff', borderRadius: '10px', padding: '1px 5px', fontSize: '10px', fontWeight: '700', lineHeight: '14px', minWidth: '14px', textAlign: 'center', border: '2px solid #fff' }}>
                    {necitite > 9 ? '9+' : necitite}
                  </span>
                )}
              </NavLink>
            )}

            {/* Buton /info pentru cetățeni */}
            {isCetatean && (
              <NavLink to="/info" style={{ fontSize: '13px', color: '#6366f1', textDecoration: 'none', fontWeight: '600', whiteSpace: 'nowrap' }} title="Informații utile">
                ℹ️ Info
              </NavLink>
            )}

            {!isMobile && (
              <button onClick={handleLogout} style={theme.logoutBtn}>
                ↩ Ieși
              </button>
            )}
          </div>
        </header>

        {/* Overlay menu mobil */}
        {isMobile && menuOpen && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 200 }} onClick={() => setMenuOpen(false)}>
            <div style={{ ...theme.sidebar, position: 'absolute', top: 0, left: 0, bottom: 0, width: '280px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 20px 12px' }}>
                <span style={{ fontWeight: '700', color: theme.brandText.color, fontSize: '16px' }}>{isCetatean ? 'Portal Cetățean' : 'Intranet Primărie'}</span>
                <button onClick={() => setMenuOpen(false)} style={{ background: 'none', border: 'none', color: theme.navLink.color, fontSize: '20px', cursor: 'pointer' }}>✕</button>
              </div>
              {user && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '0 20px 16px', borderBottom: `1px solid ${isCetatean ? '#c7d2fe' : '#2d2d4e'}`, marginBottom: '8px' }}>
                  <span style={{ fontSize: '22px' }}>{isCetatean ? (user.tip_persoana === 'juridica' ? '🏢' : '👤') : '🛠'}</span>
                  <div>
                    <div style={{ color: theme.userName.color, fontWeight: 600, fontSize: '14px' }}>{user.prenume} {user.nume}</div>
                    <div style={{ color: theme.userRole.color, fontSize: '12px' }}>{user.email}</div>
                  </div>
                </div>
              )}
              <nav style={{ flex: 1, overflowY: 'auto' }}>
                {navItems.map(item => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setMenuOpen(false)}
                    style={({ isActive }) => ({
                      display: 'flex', alignItems: 'center', gap: '14px',
                      color: isActive ? (isCetatean ? '#312e81' : '#fff') : theme.navLink.color,
                      textDecoration: 'none', fontSize: '15px', padding: '13px 20px',
                      borderBottom: `1px solid ${isCetatean ? '#e0e7ff' : '#16162a'}`,
                      backgroundColor: isActive ? (isCetatean ? '#e0e7ff' : '#2d2d7e') : 'transparent',
                    })}
                  >
                    <span style={{ fontSize: '18px', width: '28px' }}>{item.icon}</span>
                    {item.label}
                  </NavLink>
                ))}
              </nav>
              <button onClick={handleLogout} style={{ margin: '16px', padding: '14px', background: 'none', border: `1px solid ${isCetatean ? '#c7d2fe' : '#2d2d4e'}`, color: theme.navLink.color, borderRadius: '10px', cursor: 'pointer', fontSize: '15px' }}>
                ↩ Deconectare
              </button>
            </div>
          </div>
        )}

        <main style={{ flex: 1, padding: isMobile ? '16px' : '24px 28px' }}>{children}</main>
      </div>
    </div>
  );
}

// ── Tema cetățean (luminos, albastru deschis) ─────────────────
const thC = {
  sidebar: {
    backgroundColor: '#fff',
    borderRight: '1px solid #e0e7ff',
    padding: '24px 0',
  },
  brand: { display: 'flex', alignItems: 'center', gap: '10px', padding: '0 20px 8px' },
  brandText: { color: '#312e81', fontWeight: '700', fontSize: '15px' },
  brandSub:  { color: '#818cf8', fontSize: '11px', marginTop: '1px' },
  userCard:  { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 20px 16px', borderBottom: '1px solid #e0e7ff', marginBottom: '8px' },
  userName:  { color: '#1e1b4b', fontWeight: '600', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  userRole:  { color: '#818cf8', fontSize: '11px' },
  navLink:   { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', color: '#4b5563', textDecoration: 'none', fontSize: '14px', transition: 'all .15s', borderRadius: '0' },
  navActive: { color: '#312e81', backgroundColor: '#eef2ff', borderRight: '3px solid #6366f1', fontWeight: '600' },
  topBar:    { backgroundColor: '#fff', borderBottom: '1px solid #e0e7ff' },
  topBarTextColor: '#1e1b4b',
  logoutBtn: { padding: '7px 14px', background: 'none', border: '1.5px solid #e0e7ff', color: '#6b7280', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' },
  sidebarLogout: { margin: '12px 16px', padding: '10px', background: 'none', border: '1px solid #e0e7ff', color: '#6b7280', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
};

// ── Tema angajat (întunecat, profesional) ─────────────────────
const thA = {
  sidebar: {
    backgroundColor: '#1a1a2e',
    padding: '24px 0',
    color: '#fff',
  },
  brand: { display: 'flex', alignItems: 'center', gap: '10px', padding: '0 20px 8px' },
  brandText: { color: '#fff', fontWeight: '700', fontSize: '15px' },
  brandSub:  { color: '#d0d0f0', fontSize: '11px', marginTop: '1px' },
  userCard:  { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 20px 16px', borderBottom: '1px solid #2d2d4e', marginBottom: '8px' },
  userName:  { color: '#fff', fontWeight: '600', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  userRole:  { color: '#d0d0f0', fontSize: '11px' },
  navLink:   { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', color: '#d0d0f0', textDecoration: 'none', fontSize: '14px', transition: 'all .15s' },
  navActive: { color: '#fff', backgroundColor: '#2d2d7e', borderRight: '3px solid #7f77dd' },
  topBar:    { backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb' },
  topBarTextColor: '#374151',
  logoutBtn: { padding: '7px 14px', background: 'none', border: '1.5px solid #e5e7eb', color: '#6b7280', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' },
  sidebarLogout: { margin: '12px 16px', padding: '10px', background: 'none', border: '1px solid #3d3d5e', color: '#d0d0f0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
};
